import { RJSFSchema, RegistryWidgetsType, UiSchema } from '@rjsf/utils';

import BookingNumberSearchWidget from './BookingNumberSearchWidget';

// Reason types enum values matching the backend
export const REASON_TYPES = {
  SURCHARGES_FEES_NOT_LOADED_CORRECTLY: 'SURCHARGES_FEES_NOT_LOADED_CORRECTLY',
  INCORRECT_INCLUSIONS: 'INCORRECT_INCLUSIONS',
  INCORRECT_BOOKING_DATES: 'INCORRECT_BOOKING_DATES',
  CANCELLATION_NOT_CONFIRMED: 'CANCELLATION_NOT_CONFIRMED',
  CUSTOMER_COMPLAINT: 'CUSTOMER_COMPLAINT',
} as const;

export type ReasonType = (typeof REASON_TYPES)[keyof typeof REASON_TYPES];

// Friendly display labels for each reason type
export const REASON_LABELS: Record<ReasonType, string> = {
  [REASON_TYPES.SURCHARGES_FEES_NOT_LOADED_CORRECTLY]: 'Surcharges/fees not loaded correctly - Commercial',
  [REASON_TYPES.INCORRECT_INCLUSIONS]: 'Incorrect Inclusions - Marketing',
  [REASON_TYPES.INCORRECT_BOOKING_DATES]: 'Incorrect booking dates - Commercial',
  [REASON_TYPES.CANCELLATION_NOT_CONFIRMED]: 'Cancellation not confirmed with vendor - Customer Service',
  [REASON_TYPES.CUSTOMER_COMPLAINT]: 'Customer complaint - Customer Service',
};

// Generate enum names in the same order as enum values
const REASON_ENUM_NAMES = Object.values(REASON_TYPES).map((value) => REASON_LABELS[value]);

/**
 * Get the friendly display label for a reason type.
 * Returns the raw value if not found, or a dash for null/undefined values.
 */
export function getReasonLabel(reason: string | null | undefined): string {
  if (!reason) {
    return '-';
  }
  return REASON_LABELS[reason as ReasonType] || reason;
}

// Shared UI schema for error payment forms
export const errorPaymentUiSchema: UiSchema = {
  fk_offer: { 'ui:widget': 'hidden' },
  fk_vendor: { 'ui:widget': 'hidden' },
  fk_added_by: { 'ui:widget': 'hidden' },
  date_of_adjustment: {
    'ui:placeholder': '31/12/2017',
    'ui:widget': 'date',
    'ui:title': 'Date of Payment',
  },
  currency: {
    'ui:widget': 'select',
    'ui:title': 'Payment currency',
  },
  amount: {
    'ui:placeholder': '0',
    'ui:widget': 'text',
    'ui:title': 'Payment amount',
  },
  reason: {
    'ui:widget': 'select',
    'ui:title': 'Reason',
    'ui:placeholder': 'Select a reason',
    'ui:enumNames': REASON_ENUM_NAMES,
  },
  le_booking_number: {
    'ui:widget': 'bookingNumberSearch',
    'ui:title': 'LE Booking Number',
    'ui:placeholder': 'Search by booking number...',
  },
  description: {
    'ui:widget': 'textarea',
    'ui:title': 'Description',
  },
};

// Shared widgets for error payment forms
export const errorPaymentWidgets: RegistryWidgetsType = {
  bookingNumberSearch: BookingNumberSearchWidget,
};

/**
 * Enhance the backend schema with reason enum and booking number fields.
 */
export function enhanceErrorPaymentSchema(schema: RJSFSchema): RJSFSchema {
  if (!schema) {
    return schema;
  }

  const reasonEnumValues = Object.values(REASON_TYPES);
  const schemaProperties = (schema.properties || {}) as Record<string, RJSFSchema>;

  return {
    ...schema,
    properties: {
      ...schemaProperties,
      reason: {
        ...(schemaProperties.reason || {}),
        type: 'string' as const,
        title: 'Reason',
        enum: reasonEnumValues,
      },
      le_booking_number: {
        ...(schemaProperties.le_booking_number || {}),
        type: 'string' as const,
        title: 'LE Booking Number',
      },
    },
  };
}
